<template>
  <el-row :gutter="15">
    <el-col :span="24">
      <el-card shadow="never">
        <el-table
          v-loading="isLoading"
          :data="holidays"
          stripe
          class="w-100"
        >
          <el-table-column
            label="Name"
            prop="name"
            :formatter="row => row.name || '-'"
          />
          <el-table-column
            label="Date"
            prop="date"
            :formatter="row => formatDate(row.date)"
          />
          <el-table-column
            label="State"
            prop="state"
            :formatter="row => row.state || 'All'"
          />
          <el-table-column
            label="Created"
            prop="created_at"
            :formatter="row => row.created_at ? $moment(row.created_at).fromNow() : '-'"
          />
          <el-table-column
            v-if="canUpdate || canDelete"
            label="Actions"
            align="right"
            width="180"
          >
            <template slot-scope="scope">
              <el-button
                v-if="canUpdate"
                size="mini"
                @click.native="edit(scope.row)"
              >
                Edit
              </el-button>
              <el-button
                v-if="canDelete"
                size="mini"
                type="danger"
                @click.native="remove(scope.row)"
              >
                Delete
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <br>

        <div style="text-align: center;">
          <el-pagination
            :current-page.sync="holidays.page"
            :page-size.sync="holidays.limit"
            layout="total, sizes, prev, pager, next, jumper"
            :total="holidays.total"
            @size-change="reload"
            @current-change="reload"
          />
        </div>
      </el-card>
    </el-col>

    <el-dialog
      :title="selectedHoliday.id ? 'Edit Holiday' : 'Create Holiday'"
      width="620px"
      :visible.sync="isDialogVisible"
      :close-on-click-modal="false"
    >
      <el-form
        ref="holidayForm"
        :model="selectedHoliday"
        :rules="rules"
        label-width="120px"
      >
        <el-form-item
          label="Name"
          prop="name"
        >
          <el-input v-model="selectedHoliday.name" />
        </el-form-item>

        <el-form-item
          label="Date"
          prop="date"
        >
          <el-date-picker
            v-model="selectedHoliday.date"
            type="date"
            value-format="yyyy-MM-dd"
            class="w-100"
          />
        </el-form-item>

        <el-form-item label="State">
          <el-select
            v-model="selectedHoliday.state"
            clearable
            class="w-100"
            placeholder="National if blank"
          >
            <el-option
              v-for="state in states"
              :key="state"
              :label="state"
              :value="state"
            />
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="isDialogVisible = false">
          Cancel
        </el-button>
        <el-button
          type="primary"
          :loading="isSaving"
          @click="save"
        >
          Save
        </el-button>
      </template>
    </el-dialog>
  </el-row>
</template>

<script>
import { DELETE, UPDATE } from '@/plugins/user'

export default {
  name: 'Holidays',
  data () {
    return {
      title: 'Holidays',
      breadcrumb: {
        name: 'Holidays',
        route: { name: 'configuration-holidays' }
      },
      actions: [
        {
          hidden: false,
          name: 'Create',
          icon: ['fal', 'plus'],
          event: () => this.create()
        },
        {
          hidden: false,
          name: 'Refresh',
          icon: ['fal', 'sync'],
          event: () => this.reload()
        }
      ],
      holidays: [],
      isLoading: false,
      isSaving: false,
      isDialogVisible: false,
      selectedHoliday: new this.$sdk.holiday(),
      states: ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'],
      rules: {
        name: [
          { required: true, message: 'Name is required', trigger: 'blur' }
        ],
        date: [
          { required: true, message: 'Date is required', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    canUpdate () {
      return this.$user.can(UPDATE, this.$sdk.holiday) || true
    },
    canDelete () {
      return this.$user.can(DELETE, this.$sdk.holiday) || true
    }
  },
  created () {
    this.load()
  },
  methods: {
    formatDate (value) {
      return value ? this.$moment(value).format('ll') : '-'
    },
    create () {
      this.selectedHoliday = new this.$sdk.holiday()
      this.isDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.holidayForm) {
          this.$refs.holidayForm.clearValidate()
        }
      })
    },
    edit (holiday) {
      this.selectedHoliday = holiday.clone()
      this.isDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.holidayForm) {
          this.$refs.holidayForm.clearValidate()
        }
      })
    },
    load () {
      this.isLoading = true

      this.$sdk.holiday.list(builder => {
        builder.order = 'date:desc'
      })
        .then(res => {
          this.holidays = res
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    async reload () {
      if (!this.holidays.reload) {
        this.load()
        return
      }

      this.isLoading = true

      try {
        await this.holidays.reload(builder => {
          builder.order = 'date:desc'
        })
      } finally {
        this.isLoading = false
      }
    },
    async save () {
      const isValid = await new Promise(resolve => {
        this.$refs.holidayForm.validate(valid => resolve(valid))
      })

      if (!isValid) {
        return
      }

      this.isSaving = true

      try {
        await this.selectedHoliday.save()
        this.$message({ message: 'Holiday saved', type: 'success' })
        this.isDialogVisible = false
        await this.reload()
      } catch (error) {
        this.$message.error('Failed to save holiday')
      } finally {
        this.isSaving = false
      }
    },
    remove (holiday) {
      this.$confirm('Are you sure you want to delete this holiday?', 'Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      })
        .then(async () => {
          this.isLoading = true

          try {
            await holiday.delete()
            this.$message({ message: 'Holiday deleted', type: 'success' })
            await this.reload()
          } catch (error) {
            this.$message.error('Failed to delete holiday')
          } finally {
            this.isLoading = false
          }
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
</style>
